/**
 * Vello public institutional site (ADR-0030), faithful to the authoritative Claude Design
 * `Site Institucional.dc.html` (S01 Home). The market + language resolve via the neutral `@vello/web-entry`
 * kernel (`resolveSiteEntry`); a served market renders its injected `SiteContent`, an unserved one renders the
 * neutral "choose a market" state. The visitor lands on the canonical `/{country}/{locale}` URL: when the
 * resolution suggests a soft redirect (`suggestEntryRedirect`), the app pushes that path (a soft, overridable
 * client nav — never a hard 302), so e.g. a pt-BR visitor to Brazil settles on `/br/pt-br`.
 *
 * Pages (X-03, D439; site completion): after the prefix, one catalog-localized slug may name a page
 * (`src/site-routes.ts`): the design's S02 Motoristas, S03 Vello Business and S04 Segurança, the help, about, press,
 * careers, sign-in and download pages (`SitePages.tsx`, content from the market's pack), and the public account-deletion
 * page (`AccountDeletion.tsx`). Every link and button goes somewhere (`SiteChrome.tsx` `SiteLinkTo`): in-site links
 * navigate on the client (pushState, then scroll to the named section or the top), back/forward re-resolves the page,
 * the Central chat opens the widget when the site carries it, and nginx's SPA fallback serves every deep link.
 *
 * Neutral by construction: this file hardcodes no market — every string comes from either the injected
 * `SiteContent` (a country pack) or the base-language chrome catalog. Every color is a `@vello/tokens`
 * variable (the visual gate enforces it); the navy brand ink is the token default.
 */
import { type MouseEvent, type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import wordmarkInk from '@vello/brand/assets/wordmark/svg/vello-wordmark-ink.svg';
import { buildEntryPath, type SiteContent } from '@vello/web-entry';
import { resolveSiteEntry, countryLocales, sitePageOf, legalPagePath, widgetScriptAttributes, type ResolvedSiteTarget, type SitePage, type SiteRegistry } from '../src/index.js';
import { readEntryInputs, saveCountry, saveLocale, contentForCountry, resolveApiBaseUrl, phonePrefixFor, type SiteRuntimeConfig } from './config';
import { createCopy, pageSlugId, selectSiteCatalog, sitePageSlugs, type Copy } from './i18n';
import { AccountDeletionPage } from './AccountDeletion';
import { PAGE_CONTENT_KEY, SiteFrame, SiteNavProvider, type SiteNav } from './SiteChrome';
import { BusinessPage, DownloadPage, DriversPage, HelpPage, SafetyPage, SignInPage, TextPage } from './SitePages';
import { HomePage } from './HomePage';
import { CareersPage } from './CareersPage';

/** The localized slugs of every page (any served language's word reaches the page). */
const PAGE_SLUGS = sitePageSlugs();

type Navigate = (event: MouseEvent<HTMLAnchorElement>, href: string) => void;

/** The in-site link of a page: its `/{country}/{locale}/{slug}` path, or the bare slug before a market resolves. */
function pageHref(country: string, locale: string, slug: string): string {
  if (country === '' || locale === '') return slug === '' ? '/' : `/${slug}`;
  return buildEntryPath(country, locale, slug === '' ? '/' : slug);
}

const primaryLang = (tag: string): string => tag.toLowerCase().split('-')[0] ?? tag;

/** The localized country name for a code, via `Intl.DisplayNames` (no hardcoded name — neutral, locale-aware). */
function countryName(code: string, locale: string): string {
  if (code === '') return '—';
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code.toUpperCase()) ?? code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
}

/** Sync `<html lang>`, a canonical link, and the hreflang alternate set into the document head. */
function useSeoHead(canonical: string, hreflang: readonly { readonly hreflang: string; readonly href: string }[], lang: string): void {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = lang;
    const head = document.head;
    for (const el of Array.from(head.querySelectorAll('link[data-vello-seo]'))) el.remove();
    const add = (rel: string, href: string, hl?: string): void => {
      const link = document.createElement('link');
      link.setAttribute('data-vello-seo', '');
      link.rel = rel;
      link.href = href;
      if (hl !== undefined) link.hreflang = hl;
      head.appendChild(link);
    };
    add('canonical', canonical);
    for (const alt of hreflang) add('alternate', alt.href, alt.hreflang);
  }, [canonical, hreflang, lang]);
}

declare global {
  interface Window {
    /** The Central widget's handle (`apps/central-widget` loader), present once `/widget.js` has mounted. */
    VelloCentral?: { open: () => void; close: () => void };
  }
}

/** Whether the market supplies a page (home and account deletion always exist). */
function pageAvailable(content: SiteContent, page: SitePage): boolean {
  const key = PAGE_CONTENT_KEY[page];
  return key === undefined || content.pages?.[key] !== undefined;
}

/** The page body for a served market; a page the market does not supply renders the home page. */
function PageBody({ page, content }: { page: SitePage; content: SiteContent }): ReactElement {
  const p = content.pages;
  switch (page) {
    case 'drivers': if (p?.drivers !== undefined) return <DriversPage page={p.drivers} />; break;
    case 'business': if (p?.business !== undefined) return <BusinessPage page={p.business} />; break;
    case 'safety': if (p?.safety !== undefined) return <SafetyPage page={p.safety} />; break;
    case 'help': if (p?.help !== undefined) return <HelpPage page={p.help} />; break;
    case 'about': if (p?.about !== undefined) return <TextPage page={p.about} />; break;
    case 'press': if (p?.press !== undefined) return <TextPage page={p.press} />; break;
    case 'careers': if (p?.careers !== undefined) return <CareersPage page={p.careers} />; break;
    case 'sign-in': if (p?.signIn !== undefined) return <SignInPage page={p.signIn} />; break;
    case 'download': if (p?.download !== undefined) return <DownloadPage page={p.download} content={content} />; break;
    default: break;
  }
  return <HomePage content={content} />;
}

/** The browser tab title of a page: its own title, then the brand. */
function pageTitle(page: SitePage, content: SiteContent): string {
  const p = content.pages;
  const titles: Partial<Record<SitePage, string | undefined>> = {
    drivers: content.nav.find((n) => n.key === 'drivers')?.label,
    business: content.nav.find((n) => n.key === 'business')?.label,
    safety: content.nav.find((n) => n.key === 'safety')?.label,
    help: p?.help?.title,
    about: p?.about?.title,
    press: p?.press?.title,
    careers: p?.careers?.title,
    'sign-in': p?.signIn?.title,
    download: p?.download?.title,
  };
  const title = titles[page];
  return title !== undefined && title !== '' ? `${title} · Vello` : 'Vello';
}

/** Scroll to a section of the current page (below the sticky nav, see `scroll-margin-top`), or to the top. */
function scrollToSection(section: string | undefined): void {
  if (typeof window === 'undefined') return;
  const el = section !== undefined ? document.getElementById(section) : null;
  if (el !== null && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
  else if (typeof window.scrollTo === 'function') window.scrollTo(0, 0);
}

/** A served market: the nav/footer frame, the page, and how every pack destination resolves. */
function ServedSite({
  page,
  content,
  t,
  country,
  locale,
  widgetEnabled,
  apiBaseUrl,
  onNavigate,
  deletion,
}: {
  page: SitePage;
  content: SiteContent;
  t: Copy;
  country: string;
  locale: string;
  widgetEnabled: boolean;
  apiBaseUrl: string;
  onNavigate: Navigate;
  deletion: { readonly href: string; readonly label: string; readonly onNavigate: Navigate };
}): ReactElement {
  const shown: SitePage = pageAvailable(content, page) ? page : 'home';

  useEffect(() => {
    if (typeof document !== 'undefined') document.title = pageTitle(shown, content);
  }, [shown, content]);

  const nav = useMemo<SiteNav>(() => {
    const hrefOf = (target: ResolvedSiteTarget): string | undefined => {
      switch (target.kind) {
        case 'page': {
          if (target.page === 'account-deletion') return deletion.href;
          if (!pageAvailable(content, target.page)) return undefined;
          const path = pageHref(country, locale, target.page === 'home' ? '' : t(pageSlugId(target.page)));
          return target.section !== undefined ? `${path}#${target.section}` : path;
        }
        case 'legal':
          return country === '' ? undefined : legalPagePath(country, 'RIDER', target.document);
        case 'contact':
          // The chat opens on click when the widget is on the page; the address (and the fallback) is the help page.
          return pageAvailable(content, 'help') && content.pages?.help !== undefined ? pageHref(country, locale, t(pageSlugId('help'))) : undefined;
        case 'external':
          return target.href;
        default:
          return undefined;
      }
    };
    const go = (event: MouseEvent<HTMLAnchorElement>, target: ResolvedSiteTarget): void => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (target.kind === 'contact' && widgetEnabled && typeof window !== 'undefined' && window.VelloCentral !== undefined) {
        event.preventDefault();
        window.VelloCentral.open();
        return;
      }
      if (target.kind === 'page' || target.kind === 'contact') {
        const href = hrefOf(target);
        if (href !== undefined) onNavigate(event, href);
      }
      // A legal text and an outside address are ordinary links (the legal page boots on its own).
    };
    return { page: shown, content, t, locale, hrefOf, go, widgetEnabled, country, apiBaseUrl };
  }, [shown, content, t, country, locale, widgetEnabled, apiBaseUrl, onNavigate, deletion.href]);

  return (
    <SiteNavProvider value={nav}>
      <SiteFrame deletion={deletion}>
        <PageBody page={shown} content={content} />
      </SiteFrame>
    </SiteNavProvider>
  );
}

export function App({ config }: { config: SiteRuntimeConfig }): ReactElement {
  const [explicitCountry, setExplicitCountry] = useState<string | undefined>(undefined);
  const [explicitLocale, setExplicitLocale] = useState<string | undefined>(undefined);
  // The current path: the entry + page re-resolve on an in-site navigation (pushState) and on back/forward.
  const [pathname, setPathname] = useState<string>(() => (typeof window !== 'undefined' ? window.location.pathname : '/'));

  const registry: SiteRegistry = config.registry;
  const entry = useMemo(
    () => resolveSiteEntry(registry, { ...readEntryInputs(config, { explicitCountry, explicitLocale }), pathname }),
    [config, registry, explicitCountry, explicitLocale, pathname],
  );
  const t = useMemo(() => createCopy(selectSiteCatalog(entry.locale)), [entry.locale]);
  const page = sitePageOf(registry, pathname, PAGE_SLUGS);

  useSeoHead(entry.canonical, entry.hreflang, primaryLang(entry.locale));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onPop = (): void => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Suggest, don't lock (ADR-0030 §2): settle the URL on the canonical /{country}/{locale} path with a soft,
  // overridable client nav (replaceState) — never a hard redirect. A crawler / already-localized path yields
  // redirect:false, so a deep or shared link renders itself unrewritten.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (entry.redirect.redirect && entry.redirect.to !== undefined && window.location.pathname !== entry.redirect.to) {
      window.history.replaceState(null, '', entry.redirect.to);
      setPathname(entry.redirect.to);
    }
  }, [entry.redirect.redirect, entry.redirect.to]);

  // A scroll request after a client navigation: the section to show (or the top), re-armed by `seq`.
  const [scroll, setScroll] = useState<{ readonly section: string | undefined; readonly seq: number }>(() => ({
    section: typeof window !== 'undefined' && window.location.hash.length > 1 ? decodeURIComponent(window.location.hash.slice(1)) : undefined,
    seq: 0,
  }));
  useEffect(() => {
    // The first render (a deep link with a #section) only scrolls when it names one; later requests always scroll.
    if (scroll.seq === 0 && scroll.section === undefined) return;
    scrollToSection(scroll.section);
  }, [scroll]);

  /** An in-site link: a plain click becomes a client navigation; a modified click (new tab, …) stays the browser's. */
  const onNavigate = useCallback<Navigate>((event, href) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    window.history.pushState(null, '', href);
    const [path = '/', section] = href.split('#');
    setPathname(path);
    setScroll((prev) => ({ section: section !== undefined && section !== '' ? section : undefined, seq: prev.seq + 1 }));
  }, []);

  const homeHref = pageHref(entry.country, entry.locale, '');
  const deletionHref = pageHref(entry.country, entry.locale, t('site.route.accountDeletion'));

  // The public account-deletion page (X-03) is served for every visitor — a served market or not: it needs only the
  // neutral chrome copy, and the phone number (not the site's market) decides where the code goes.
  if (page === 'account-deletion') {
    return (
      <AccountDeletionPage
        t={t}
        locale={entry.locale}
        apiBaseUrl={resolveApiBaseUrl(config)}
        phonePrefix={phonePrefixFor(config, entry.country)}
        homeHref={homeHref}
        onNavigate={onNavigate}
      />
    );
  }

  const content = contentForCountry(config, entry.country);

  if (entry.countrySupported && content !== undefined) {
    return (
      <ServedSite
        page={page}
        content={content}
        t={t}
        country={entry.country}
        locale={entry.locale}
        widgetEnabled={widgetScriptAttributes(config.widget) !== undefined}
        apiBaseUrl={resolveApiBaseUrl(config)}
        onNavigate={onNavigate}
        deletion={{ href: deletionHref, label: t('site.footer.deleteAccount'), onNavigate }}
      />
    );
  }

  const localeOptions = countryLocales(registry, entry.country)?.supportedLocales ?? [];
  const onCountry = (code: string): void => {
    saveCountry(code);
    setExplicitCountry(code);
    setExplicitLocale(undefined);
  };
  const onLocale = (loc: string): void => {
    saveLocale(loc);
    setExplicitLocale(loc);
  };

  return (
    <div className="bootstrap">
      <header className="nav">
        <div className="nav-inner">
          <img className="nav-wordmark" src={wordmarkInk} alt="Vello" />
          <span className="nav-spacer" />
          <label className="field">
            <span className="field-label">{t('site.nav.market')}</span>
            <select className="select" value={entry.country} onChange={(e) => onCountry(e.target.value)} aria-label={t('site.nav.market')}>
              {registry.countries.length === 0 && <option value={entry.country}>{entry.country || '—'}</option>}
              {registry.countries.map((cc) => (
                <option key={cc.code} value={cc.code}>{countryName(cc.code, entry.locale)}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t('site.nav.language')}</span>
            <select className="select" value={entry.locale} onChange={(e) => onLocale(e.target.value)} aria-label={t('site.nav.language')} disabled={localeOptions.length === 0}>
              {localeOptions.length === 0 && <option value={entry.locale}>{entry.locale}</option>}
              {localeOptions.map((loc) => (<option key={loc} value={loc}>{loc}</option>))}
            </select>
          </label>
        </div>
      </header>
      <main className="bootstrap-main">
        <section className="notice" role="status">
          <h1 className="notice-title">{t('site.notAvailable.title')}</h1>
          <p className="notice-body">{t('site.notAvailable.body', { country: countryName(entry.country, entry.locale) })}</p>
          {registry.countries.length > 0 && (
            <label className="field">
              <span className="field-label">{t('site.notAvailable.browse')}</span>
              <select className="select" value="" onChange={(e) => e.target.value !== '' && onCountry(e.target.value)} aria-label={t('site.notAvailable.browse')}>
                <option value="" disabled>{t('site.notAvailable.browse')}</option>
                {registry.countries.map((cc) => (<option key={cc.code} value={cc.code}>{countryName(cc.code, entry.locale)}</option>))}
              </select>
            </label>
          )}
        </section>
      </main>
      <footer className="bootstrap-footer">
        <span className="tnum">{t('site.footer.rights')}</span>
        <a className="footer-action" href={deletionHref} onClick={(e) => onNavigate(e, deletionHref)}>{t('site.footer.deleteAccount')}</a>
      </footer>
    </div>
  );
}
